import React, { useState, useEffect, useContext } from 'react';
import { Card, Button, Form, Row, Col, Table, Alert } from 'react-bootstrap';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import AuthContext from '../../../context/AuthContext';
import { FaPlus, FaTrash, FaFilePdf, FaArrowLeft } from 'react-icons/fa';

const PaymentSchedule = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  
  const [project, setProject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // Payment schedule state
  const [deposit, setDeposit] = useState('');
  const [payments, setPayments] = useState([{ amount: '', description: '', dueDate: '' }]);
  const [finalPayment, setFinalPayment] = useState('');
  const [totalCost, setTotalCost] = useState(0);
  const [originalProjectCost, setOriginalProjectCost] = useState(0);
  
  // Fetch project data
  useEffect(() => {
    const fetchProject = async () => {
      try {
        const response = await fetch(`/api/projects/${projectId}`, {
          headers: {
            'x-auth-token': token
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch project details');
        }
        
        const data = await response.json();
        setProject(data);
        
        // Store the original project total cost
        const projectTotal = (parseFloat(data.SumOfTimeAndMaterials) || 0) + (parseFloat(data.GeneralRequirement) || 0);
        setOriginalProjectCost(projectTotal);
        
        // Load existing payment schedule if available
        const scheduleResponse = await fetch(`/api/projects/${projectId}/payment-schedule`, {
          headers: {
            'x-auth-token': token
          }
        });
        
        if (scheduleResponse.ok) {
          const scheduleData = await scheduleResponse.json();
          if (scheduleData) {
            setDeposit(scheduleData.deposit || '');
            setPayments(scheduleData.payments.length > 0 ? scheduleData.payments : [{ amount: '', description: '', dueDate: '' }]);
            setFinalPayment(scheduleData.finalPayment || '');
          }
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        toast.error('Failed to load project data');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchProject();
  }, [projectId, token]);
  
  // Calculate total cost when values change
  useEffect(() => {
    let total = 0;
    
    // Add deposit
    if (deposit) {
      total += parseFloat(deposit.replace(/[^0-9.-]+/g, '')) || 0;
    }
    
    // Add all payments
    payments.forEach(payment => {
      if (payment.amount) {
        total += parseFloat(payment.amount.replace(/[^0-9.-]+/g, '')) || 0;
      }
    });
    
    // Add final payment
    if (finalPayment) {
      total += parseFloat(finalPayment.replace(/[^0-9.-]+/g, '')) || 0;
    }
    
    setTotalCost(total);
  }, [deposit, payments, finalPayment]);
  
  // Handle deposit change - just capture raw input
  const handleDepositChange = (e) => {
    const inputValue = e.target.value;
    // Allow any characters during typing
    setDeposit(inputValue);
  };
  
  // Handle final payment change - just capture raw input
  const handleFinalPaymentChange = (e) => {
    const inputValue = e.target.value;
    // Allow any characters during typing
    setFinalPayment(inputValue);
  };
  
  // Handle payment change
  const handlePaymentChange = (index, field, value) => {
    const updatedPayments = [...payments];
    
    if (field === 'amount') {
      // Allow any characters during typing
      updatedPayments[index][field] = value;
    } else {
      updatedPayments[index][field] = value;
    }
    
    setPayments(updatedPayments);
  };
  
  // Format all currency inputs when focus is lost
  const handleCurrencyBlur = (value, setter) => {
    if (!value) {
      setter('');
      return;
    }
    
    // Extract numeric value, ignoring non-numeric characters
    const numericValue = value.replace(/[^0-9.]/g, '');
    
    if (!numericValue) {
      setter('');
      return;
    }
    
    // Format as currency
    try {
      const floatValue = parseFloat(numericValue);
      if (!isNaN(floatValue)) {
        setter('$' + floatValue.toLocaleString('en-US', { 
          minimumFractionDigits: 2, 
          maximumFractionDigits: 2 
        }));
      } else {
        setter('');
      }
    } catch (e) {
      setter('');
    }
  };
  
  // Add a new payment
  const addPayment = () => {
    setPayments([...payments, { amount: '', description: '', dueDate: '' }]);
  };
  
  // Remove a payment
  const removePayment = (index) => {
    if (payments.length === 1) {
      setPayments([{ amount: '', description: '', dueDate: '' }]);
    } else {
      const updatedPayments = [...payments];
      updatedPayments.splice(index, 1);
      setPayments(updatedPayments);
    }
  };
  
  // Save payment schedule
  const savePaymentSchedule = async () => {
    // Format all currency values before saving
    handleCurrencyBlur(deposit, setDeposit);
    
    // Properly format payments before saving
    const updatedPayments = [...payments];
    updatedPayments.forEach((payment, index) => {
      if (payment.amount) {
        handleCurrencyBlur(payment.amount, (val) => {
          updatedPayments[index].amount = val;
        });
      }
    });
    setPayments(updatedPayments);
    
    handleCurrencyBlur(finalPayment, setFinalPayment);
    
    // Need to wait a moment for state updates to complete
    await new Promise(resolve => setTimeout(resolve, 0));
    
    // Check if total cost matches original project cost
    if (originalProjectCost > 0 && Math.abs(totalCost - originalProjectCost) > 0.01) {
      const confirmed = window.confirm(
        `Your payment schedule total (${totalCost.toLocaleString('en-US', { minimumFractionDigits: 2 })}) does not match your total cost of the project when created (${originalProjectCost.toLocaleString('en-US', { minimumFractionDigits: 2 })}).\n\nDo you want to continue?`
      );
      if (!confirmed) {
        return;
      }
    }
    
    setIsSaving(true);
    
    try {
      const response = await fetch(`/api/projects/${projectId}/payment-schedule`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        body: JSON.stringify({
          deposit,
          payments: updatedPayments, // Use the updated payments
          finalPayment,
          totalCost
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        console.error('Server error:', errorData);
        throw new Error(errorData?.msg || 'Failed to save payment schedule');
      }
      
      toast.success('Payment schedule saved successfully');
      
      // Navigate back to project page on documents tab
      navigate(`/projects/${projectId}`, { state: { activeTab: 'documents' } });
    } catch (err) {
      console.error('Error saving payment schedule:', err);
      toast.error(err.message || 'Failed to save payment schedule');
    } finally {
      setIsSaving(false);
    }
  };
  
  // Generate payment schedule document
  const generateDocument = async () => {
    // Format all currency values before generating
    handleCurrencyBlur(deposit, setDeposit);
    const formattedPayments = payments.map(p => ({
      ...p,
      amount: p.amount ? handleCurrencyBlur(p.amount, () => {}) || p.amount : ''
    }));
    setPayments(formattedPayments);
    handleCurrencyBlur(finalPayment, setFinalPayment);
    
    setIsGenerating(true);
    
    try {
      const response = await fetch(`/api/projects/${projectId}/payment-schedule/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        body: JSON.stringify({
          deposit,
          payments,
          finalPayment,
          totalCost
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to generate payment schedule document');
      }
      
      const data = await response.json();
      
      // Use a fetch call to download the file
      const downloadResponse = await fetch(`/api/documents/${data.documentId}/download`, {
        headers: {
          'x-auth-token': token
        }
      });
      
      if (!downloadResponse.ok) {
        throw new Error('Failed to download document');
      }
      
      // Get the file data
      const blob = await downloadResponse.blob();
      
      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', data.fileName || 'payment_schedule.docx');
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Payment schedule document generated successfully');
    } catch (err) {
      console.error('Error generating payment schedule document:', err);
      toast.error('Failed to generate payment schedule document');
    } finally {
      setIsGenerating(false);
    }
  };
  
  if (isLoading) {
    return <div className="text-center mt-5">Loading project details...</div>;
  }
  
  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2>Payment Schedule</h2>
          <h5 className="text-muted">
            Project: <Link to={`/projects/${projectId}`}>{project.ProjectName}</Link>
          </h5>
        </div>
        <div>
          <Button 
            variant="outline-secondary" 
            className="me-2"
            as={Link}
            to={`/projects/${projectId}`}
          >
            <FaArrowLeft className="me-1" />
            Back to Project
          </Button>
          <Button 
            variant="primary" 
            onClick={savePaymentSchedule}
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Schedule'}
          </Button>
        </div>
      </div>
      
      <Card className="mb-4">
        <Card.Body>
          <Form>
            <Form.Group className="mb-4">
              <Form.Label><strong>Deposit</strong></Form.Label>
              <Form.Control
                type="text"
                value={deposit}
                onChange={handleDepositChange}
                onBlur={() => handleCurrencyBlur(deposit, setDeposit)}
                placeholder="$0.00"
              />
            </Form.Group>
            
            <h5 className="mb-3">Progress Payments</h5>
            
            {payments.map((payment, index) => (
              <Row key={index} className="mb-3 align-items-end">
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Amount</Form.Label>
                    <Form.Control
                      type="text"
                      value={payment.amount}
                      onChange={(e) => handlePaymentChange(index, 'amount', e.target.value)}
                      onBlur={() => {
                        const updatedPayments = [...payments];
                        handleCurrencyBlur(payment.amount, (val) => {
                          updatedPayments[index].amount = val;
                          setPayments(updatedPayments);
                        });
                      }}
                      placeholder="$0.00"
                    />
                  </Form.Group>
                </Col>
                <Col md={5}>
                  <Form.Group>
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                      type="text"
                      value={payment.description}
                      onChange={(e) => handlePaymentChange(index, 'description', e.target.value)}
                      placeholder="Payment description or milestone"
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Due Date (Optional)</Form.Label>
                    <Form.Control
                      type="date"
                      value={payment.dueDate}
                      onChange={(e) => handlePaymentChange(index, 'dueDate', e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={1}>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => removePayment(index)}
                  >
                    <FaTrash />
                  </Button>
                </Col>
              </Row>
            ))}
            
            <div className="mb-4">
              <Button 
                variant="outline-secondary" 
                onClick={addPayment}
                className="d-flex align-items-center"
              >
                <FaPlus className="me-2" /> Add Payment
              </Button>
            </div>
            
            <Form.Group className="mb-4">
              <Form.Label><strong>Final Payment Upon Completion</strong></Form.Label>
              <Form.Control
                type="text"
                value={finalPayment}
                onChange={handleFinalPaymentChange}
                onBlur={() => handleCurrencyBlur(finalPayment, setFinalPayment)}
                placeholder="$0.00"
              />
            </Form.Group>
          </Form>
          
          <div className="border-top pt-3 mt-4">
            {originalProjectCost > 0 && (
              <h4 className="d-flex justify-content-between">
                <span>Remaining Balance:</span>
                <span style={{ 
                  color: (originalProjectCost - totalCost) === 0 
                    ? 'green' 
                    : (originalProjectCost - totalCost) < 0 
                      ? 'red' 
                      : 'inherit'
                }}>
                  {(originalProjectCost - totalCost) === 0 && '✓ '}
                  {(originalProjectCost - totalCost) < 0 ? '-' : ''}
                  ${Math.abs(originalProjectCost - totalCost).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  {(originalProjectCost - totalCost) < 0 && ' (over-allocated)'}
                </span>
              </h4>
            )}
            {originalProjectCost === 0 && (
              <h4 className="d-flex justify-content-between">
                <span>Total Scheduled:</span>
                <span className="text-primary">
                  ${totalCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </h4>
            )}
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default PaymentSchedule;